/**
 * Single probe to see if the backend is reachable. Caches result for 2 minutes
 * so we don't spam the console with repeated 500s when the backend is down.
 */
const KEY = "zolaid_backend_ok";
const FAIL_KEY = "zolaid_backend_fail_ts";
const TTL_MS = 2 * 60 * 1000;

let probePromise = null;

function isBackoffActive() {
  try {
    const ts = sessionStorage.getItem(FAIL_KEY);
    if (!ts) return false;
    return Date.now() - parseInt(ts, 10) < TTL_MS;
  } catch (_) {
    return false;
  }
}

export function isBackendLikelyDown() {
  return isBackoffActive();
}

/**
 * True when REACT_APP_API_URL looks like a doc/template value (never a real Cloud Run host).
 * Does not treat empty string as invalid â€” empty means same-origin /chat in some setups.
 */
export function isPlaceholderApiUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return false;
  const u = raw.toLowerCase();
  return (
    /your-actual|your_actual|your\.actual/.test(u) ||
    /xxxxx|placeholder|example\.com|example\.run\.app/.test(u) ||
    /zolaid-backend-xxxxx\./.test(u)
  );
}

/** Baked at build time (may be a placeholder if Cloud Build substitution was wrong). */
function getEnvApiUrl() {
  return (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
}

/**
 * When the baked env is missing or still a placeholder (must match your zolaid-backend URL).
 */
const KNOWN_PROJECT_API_FALLBACK =
  "https://zolaid-backend-525115683161.us-central1.run.app";

function readRuntimeApiBase() {
  if (typeof window === "undefined") return null;
  try {
    const w = window.__ZOLAID_API_BASE__;
    if (w != null && String(w).trim()) return String(w).trim().replace(/\/+$/, "");
    const meta = document.querySelector('meta[name="zolaid-api-base"]');
    const c = meta && meta.getAttribute("content");
    if (c && String(c).trim()) return String(c).trim().replace(/\/+$/, "");
  } catch (_) {}
  return null;
}

/**
 * Production API base: optional runtime override (window.__ZOLAID_API_BASE__ or meta zolaid-api-base),
 * then baked env, then KNOWN_PROJECT_API_FALLBACK when env is still a template.
 */
export function getConfiguredApiBase() {
  const runtime = readRuntimeApiBase();
  if (runtime && !isPlaceholderApiUrl(runtime)) return runtime;
  const env = getEnvApiUrl();
  // Non-empty env: use unless it's a template. Empty env must not fall through to "" on the live site
  // or chat will POST to /chat on the static host and get index.html (HTML instead of JSON).
  if (env && !isPlaceholderApiUrl(env)) return env;
  return KNOWN_PROJECT_API_FALLBACK;
}

/**
 * Same-origin (or relative URL on current origin) â†’ include for cookies.
 * Cross-origin â†’ omit: Bearer tokens are in headers; credentialed CORS is stricter in browsers.
 */
export function credentialsModeForApiUrl(endpoint) {
  if (typeof window === "undefined") return "include";
  try {
    const u = /^https?:\/\//i.test(endpoint)
      ? new URL(endpoint)
      : new URL(endpoint, window.location.href);
    return u.origin === window.location.origin ? "include" : "omit";
  } catch (_) {
    return "include";
  }
}

/** Use for API requests: on localhost returns "" so CRA proxy is used (avoids CORS). */
export function getApiBase() {
  if (typeof window === "undefined") {
    const env = getEnvApiUrl();
    if (env && !isPlaceholderApiUrl(env)) return env;
    return KNOWN_PROJECT_API_FALLBACK;
  }
  const origin = String(window.location?.origin || "");
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin))
    return "";
  return getConfiguredApiBase();
}

const PORT_KEY = "zolaid_backend_port";

/** Backend base URL; in dev on localhost use 8080 or stored port when REACT_APP_API_URL is unset. */
function getBackendBase(port = null) {
  if (typeof window !== "undefined") {
    const origin = (window.location && window.location.origin) || "";
    if (!/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin)) {
      return getConfiguredApiBase();
    }
  }
  let base = (process.env.REACT_APP_API_URL || "").trim().replace(/\/+$/, "");
  if (!base && typeof window !== "undefined") {
    try {
      const origin = (window.location && window.location.origin) || "";
      let p = port != null ? port : 8080;
      try {
        const stored = sessionStorage.getItem(PORT_KEY);
        if (stored && port == null) p = parseInt(stored, 10) || 8080;
      } catch (_) {}
      if (/^https?:\/\/localhost(:\d+)?$/i.test(origin)) base = `http://localhost:${p}`;
      else if (/^https?:\/\/127\.0\.0\.1(:\d+)?$/i.test(origin)) base = `http://127.0.0.1:${p}`;
    } catch (_) {}
  }
  return base;
}

/**
 * Returns a promise that resolves to true if backend is reachable, false otherwise.
 * On localhost, tries 8080 then 8081 so DevTools works when backend fell back to 8081.
 *
 * Cross-origin /health uses credentials: "omit" so a simple connectivity check does not depend
 * on credentialed CORS. Failed probes do not cache forever (so Retry / backoff expiry work).
 */
export function getBackendReady() {
  if (isBackoffActive()) {
    return Promise.resolve(false);
  }
  if (probePromise) return probePromise;

  const onLocalhost = typeof window !== "undefined" && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(String(window.location?.origin || ""));

  const base = getBackendBase();

  function markOk() {
    try {
      sessionStorage.removeItem(FAIL_KEY);
      sessionStorage.setItem(KEY, "1");
    } catch (_) {}
  }

  function markFail() {
    try {
      sessionStorage.setItem(FAIL_KEY, String(Date.now()));
    } catch (_) {}
  }

  /** Ordered list of { url, creds } â€” proxy first on localhost, then explicit ports (8080 + 8081 fallback). */
  function probeTargets() {
    const out = [];
    const seen = new Set();
    const push = (url, creds) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      out.push({ url, creds });
    };

    if (onLocalhost) {
      // CRA/webpack proxy â†’ backend :8080 (no CORS, same origin)
      push("/health", "include");
    }
    if (base) {
      push(base ? `${base}/health` : "/health", base ? "omit" : "include");
      if (/localhost:8080$/i.test(base)) {
        push(base.replace(/:8080$/i, ":8081") + "/health", "omit");
      }
      if (/127\.0\.0\.1:8080$/i.test(base)) {
        push(base.replace(/:8080$/i, ":8081") + "/health", "omit");
      }
    }
    if (onLocalhost) {
      push("http://localhost:8080/health", "omit");
      push("http://localhost:8081/health", "omit");
      push("http://localhost:8082/health", "omit");
      push("http://127.0.0.1:8080/health", "omit");
      push("http://127.0.0.1:8081/health", "omit");
      push("http://127.0.0.1:8082/health", "omit");
    }

    return out;
  }

  const run = (async () => {
    for (const { url, creds } of probeTargets()) {
      const ok = await fetch(url, { credentials: creds, mode: "cors" })
        .then((res) => res.ok)
        .catch(() => false);
      if (ok) {
        const abs = /^https?:\/\//i.test(url) ? url : "";
        const m = abs.match(/:(\d+)\/health$/);
        if (m) {
          try {
            sessionStorage.setItem(PORT_KEY, m[1]);
          } catch (_) {}
        }
        markOk();
        return true;
      }
    }
    markFail();
    return false;
  })();

  probePromise = run.then((ok) => {
    if (!ok) probePromise = null;
    return ok;
  });

  return probePromise;
}

export function clearBackendBackoff() {
  try {
    sessionStorage.removeItem(FAIL_KEY);
    sessionStorage.removeItem(KEY);
  } catch (_) {}
  probePromise = null;
}

/** Call when a backend request returns 500/fail so we back off. */
export function setBackendFailed() {
  try {
    sessionStorage.setItem(FAIL_KEY, String(Date.now()));
  } catch (_) {}
}
